'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { SearchIcon } from './Icons';
import { searchRules, splitHighlight, type FlatRule, type SearchHit } from './search';

const ease = [0.25, 0.1, 0.25, 1] as const;

function Highlight({ text, query, hitClassName }: { text: string; query: string; hitClassName: string }) {
  return (
    <>
      {splitHighlight(text, query).map((part, i) =>
        part.hit ? (
          <span key={i} className={hitClassName}>
            {part.chunk}
          </span>
        ) : (
          <span key={i}>{part.chunk}</span>
        ),
      )}
    </>
  );
}

export default function SearchBar({
  rules,
  placeholder,
  rulesLabel,
  mentionedLabel,
  resultsLabel,
  emptyLabel,
  onSelect,
}: {
  rules: FlatRule[];
  placeholder?: string;
  rulesLabel?: string;
  mentionedLabel?: string;
  resultsLabel?: string;
  emptyLabel?: string;
  onSelect: (slug: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => searchRules(rules, query), [rules, query]);
  const flat: SearchHit[] = useMemo(() => [...results.rules, ...results.mentioned], [results]);
  const showDropdown = open && query.trim().length >= 2;

  // ⌘K / Ctrl+K focuses the field, like the shortcut chip promises.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => setCursor(0), [query]);

  const pick = (hit: SearchHit) => {
    onSelect(hit.rule.slug);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flat[cursor]) pick(flat[cursor]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const renderGroup = (label: string | undefined, hits: SearchHit[], offset: number) =>
    hits.length > 0 && (
      <>
        <div className="flex w-full flex-col px-[3rem] pb-[1rem] pt-[1.5rem]">
          <p className="mb-0 font-geist text-[1.75rem] font-medium leading-[2.5rem] text-black/40">{label}</p>
        </div>
        {hits.map((hit, i) => {
          const index = offset + i;
          return (
            <button
              key={hit.rule.slug}
              type="button"
              onMouseEnter={() => setCursor(index)}
              onClick={() => pick(hit)}
              className={cn(
                'flex w-full flex-col items-start gap-[0.25rem] px-[3rem] py-[1.5rem] text-left transition-colors',
                cursor === index ? 'bg-[#e8f3ee]' : 'bg-transparent',
              )}
            >
              <p className="mb-0 font-geist text-[2rem] font-medium leading-[3rem] text-black">
                <Highlight text={hit.rule.title} query={query} hitClassName="text-[#1e8a63]" />
              </p>
              <p className="mb-0 font-geist text-[1.75rem] leading-[2.5rem] text-black/60">
                <Highlight text={hit.excerpt} query={query} hitClassName="font-medium text-black" />
              </p>
            </button>
          );
        })}
      </>
    );

  return (
    <div ref={rootRef} data-ui="rules-v3.search" className="relative z-30 flex w-full flex-col lg:w-[90rem]">
      <label
        data-ui="search.field"
        className={cn(
          'flex h-[5.5rem] w-full items-center gap-[1.75rem] rounded-[0.5rem] border border-black/10 bg-white pl-[3rem] pr-[2rem] lg:h-[8rem]',
          // Active state (Figma 13100:15631): green border + 4px 10% green ring outside the box.
          'transition-[border-color,box-shadow] focus-within:border-[#228e71] focus-within:shadow-[0_0_0_0.5rem_rgba(34,142,113,0.1)]',
        )}
      >
        <SearchIcon data-ui="search.icon" className="h-[2.5rem] w-[2.5rem] shrink-0 text-black/40" />
        <input
          ref={inputRef}
          data-ui="search.input"
          type="search"
          value={query}
          placeholder={placeholder ?? 'Search rules, terms and questions…'}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck={false}
          className="min-w-0 flex-1 bg-transparent font-geist text-[2.25rem] leading-[3.5rem] text-black caret-[#1e8a63] outline-none placeholder:text-black/40 [&::-webkit-search-cancel-button]:hidden"
        />
        {showDropdown && (
          <span className="hidden shrink-0 whitespace-nowrap font-geist text-[1.75rem] leading-[2.5rem] text-black/40 lg:block">
            {results.total} {resultsLabel ?? 'results'}
          </span>
        )}
        <span data-ui="search.shortcut" className="flex h-[3.5rem] shrink-0 items-center justify-center rounded-[0.25rem] border border-black/10 px-[0.75rem] font-geist text-[2rem] font-medium leading-[2.5rem] text-black/40">
          ⌘K
        </span>
      </label>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease }}
            className="relative mt-[1rem] flex w-full flex-col rounded-[0.5rem] border border-black/10 bg-white py-[1rem] lg:absolute lg:left-0 lg:top-[calc(100%+1.125rem)] lg:mt-0"
          >
            {results.total === 0 ? (
              <p className="mb-0 px-[3rem] py-[1.5rem] font-geist text-[2rem] leading-[3rem] text-black/40">
                {emptyLabel ?? 'No rule matches that yet.'}
              </p>
            ) : (
              <>
                {renderGroup(rulesLabel ?? 'Rules', results.rules, 0)}
                {renderGroup(mentionedLabel ?? 'Mentioned in', results.mentioned, results.rules.length)}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
